
import { VideoAnnotation, Biomechanics } from "../contracts/audio";
import { MotionEngine } from "./vision/MotionEngine";

export interface BioDiagnostic extends Biomechanics {
    anatomyScore: number;
    visualLagMs: number; 
    motionReliability: number; 
    humanFeedback: string; 
    kinematicEnvelopeStu: number[];
    kinematicEnvelopeRef: number[];
}

/**
 * analyzeBiomechanicalMatch - KINEMATIC DSP v4.6 (ULTRA-SOFT PENALTY)
 */
export const analyzeBiomechanicalMatch = async (
    stuVideo: File, 
    refVideo: File | null,
    duration: number,
    lang: 'en' | 'fr' = 'fr'
): Promise<BioDiagnostic> => {
    
    const stuEnvelope = await MotionEngine.getFullMotionEnvelope(stuVideo, duration);
    let refEnvelope: number[] = [];
    
    if (refVideo) {
        refEnvelope = await MotionEngine.getFullMotionEnvelope(refVideo, duration);
    }

    let kinematicMatch = 0;
    if (refEnvelope.length > 0 && stuEnvelope.length > 0) {
        let dot = 0; let magS = 0; let magR = 0;
        const n = Math.min(stuEnvelope.length, refEnvelope.length);
        for(let i=0; i<n; i++) {
            dot += stuEnvelope[i] * refEnvelope[i];
            magS += stuEnvelope[i] * stuEnvelope[i];
            magR += refEnvelope[i] * refEnvelope[i];
        }
        kinematicMatch = dot / (Math.sqrt(magS) * Math.sqrt(magR) || 1);
    } else {
        const avg = stuEnvelope.reduce((a, b) => a + b, 0) / (stuEnvelope.length || 1);
        // AXE : Division du coût de l'erreur par 2. Multiplicateur 1.5 au lieu de 3.0.
        kinematicMatch = Math.max(0.1, 1 - (avg * 1.5)); 
    }

    // Boost cinématique maintenu
    const finalMatch = Math.sqrt(Math.max(0, Math.min(1, kinematicMatch)));
    const anatomyScore = Math.round(finalMatch * 100);
    const isFr = lang === 'fr';

    let feedback = "";
    if (anatomyScore >= 80) {
        feedback = isFr ? "Geste stable et économe." : "Stable and economic gesture.";
    } else if (anatomyScore >= 55) {
        feedback = isFr ? "Mouvements identifiés, affinez la tenue." : "Movements identified, refine posture.";
    } else {
        feedback = isFr ? "Instabilité détectée : calmez le haut du corps." : "Instability detected: calm upper body.";
    }

    return {
        score: anatomyScore,
        anatomyScore,
        visualLagMs: 0,
        humanFeedback: feedback,
        motionReliability: finalMatch,
        postureStability: finalMatch,
        wristSuppleness: finalMatch,
        fingerEconomy: 0.8,
        fingerLandingOk: anatomyScore > 40,
        shoulderAlignment: 'RELAXED',
        neckAngleOk: true,
        bassStringMatch: true,
        checkpoints: [],
        kinematicEnvelopeStu: stuEnvelope,
        kinematicEnvelopeRef: refEnvelope,
        morphology: {
            fingerPlacementScore: anatomyScore,
            pickingAngleDelta: 0,
            stringAttackMatch: anatomyScore > 30,
            wristDeviationPct: 0
        }
    };
};
